/**
 * Natural ordering for user-visible NAME lists — the ONE comparator both the
 * client and the Functions run.
 *
 * A plain `localeCompare` walks a digit run character by character, so a wall
 * numbered 1..12 comes back as `1, 10, 11, 12, 2, 3` — an order no gym ever
 * means. `numeric: true` makes the collator read a digit run as one number,
 * which is what a human reads too: `Út 2` before `Út 10`, `U8` before `U10`,
 * `3. csoport` before `12. csoport`.
 *
 * `sensitivity: "base"` folds case and accents into ties so `piros 3` and
 * `Piros 3` stay next to each other instead of splitting the list into an
 * upper- and a lowercase half. It also matches Hungarian dictionary order,
 * where `a`/`á` differ only at the secondary level.
 *
 * Use it for anything a human reads as a name — routes, setters, groups,
 * competitors, gyms, countries. Do NOT use it for ISO dates, timestamps or
 * document ids: those are already correct as plain string comparisons, and
 * `numeric: true` would only make them slower (and, on a `YYYY-MM-DD` string,
 * subtly different).
 *
 * WHY IT IS SHARED. The ranking composer breaks a perfect tie by name, and it
 * runs on both sides: in the client bundle for the live board, and in the
 * Functions runtime for the precomputed `rankings/{groupId}` document. If the
 * two collated names differently, tied competitors would swap places between
 * the wall and the phone — the exact failure the shared composer was
 * introduced to end. One copy here removes that failure mode by construction.
 *
 * A residual a single copy cannot remove: `localeCompare` is ICU-backed, and
 * the browser's ICU and the Node runtime's are separate builds with a separate
 * default locale. Two DIFFERENT names that collate differently across those
 * two could still order differently. That is far narrower than the
 * arrival-order tie-break it replaced (which had no rule at all), and the
 * `uid` fallback in `composeGroupRanking` keeps IDENTICAL names deterministic
 * everywhere.
 *
 * Zero imports on purpose: `Intl` is the only dependency, and it exists in
 * both runtimes (Node 22 ships full ICU), which is what lets this live in the
 * pure package at all.
 */
export const NAME_COLLATION = { numeric: true, sensitivity: "base" };

/**
 * Compare two names in natural, locale-aware order.
 *
 * @param {*} a - First name (null/undefined sorts as an empty string)
 * @param {*} b - Second name
 * @param {string} [locale] - BCP-47 tag; omit for the runtime default
 * @returns {number} Negative / zero / positive, `Array.prototype.sort` style
 */
export function compareNames(a, b, locale) {
  return String(a ?? "").localeCompare(String(b ?? ""), locale, NAME_COLLATION);
}
